import { useEffect, useMemo } from 'react'
import { Controller, useForm, useWatch, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button, Field, FieldError, FieldLabel, Input } from '@clouway/ui'

import { VendorModelSelector, VendorSelector } from '@/features/vendors'
import { UnsavedChangesGuard } from '@/components/UnsavedChangesGuard'
import { TrackingObjectTypeSelector } from './TrackingObjectTypeSelector'

// The form owns its own shape and is the single validation point (the required
// message is the only domain rule here). It is intentionally decoupled from the
// wire request — vendorId only scopes the model list, and the two shapes are
// free to diverge; TrackerActivation maps form values to the request.
const buildActivationSchema = (requiredMessage: string) =>
  z.object({
    lpn: z.string().min(1, requiredMessage),
    label: z.string().min(1, requiredMessage),
    type: z.string().min(1, requiredMessage),
    msisdn: z.string().min(1, requiredMessage),
    trackingId: z.string().min(1, requiredMessage),
    vendorId: z.string().min(1, requiredMessage),
    deviceModelId: z.string().min(1, requiredMessage),
  })

export type TrackerActivationFormValues = z.infer<ReturnType<typeof buildActivationSchema>>

const emptyValues: TrackerActivationFormValues = {
  lpn: '',
  label: '',
  type: 'vehicle',
  msisdn: '',
  trackingId: '',
  vendorId: '',
  deviceModelId: '',
}

export function TrackerActivationForm({ onSubmit }: { onSubmit: SubmitHandler<TrackerActivationFormValues> }) {
  const { t } = useTranslation('feature')
  const schema = useMemo(() => buildActivationSchema(t('trackingObjects.activationForm.validationMessage')), [t])
  const { control, handleSubmit, resetField, formState } = useForm<TrackerActivationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  })
  const vendorId = useWatch({ control, name: 'vendorId' })

  // Changing vendor clears the model — the previously-picked model may not
  // belong to the new vendor (fixes the mobile DeviceTemplateForm bug).
  useEffect(() => {
    resetField('deviceModelId')
  }, [vendorId, resetField])

  return (
    <form className="mx-auto flex w-full max-w-2xl flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
      <UnsavedChangesGuard when={formState.isDirty && !formState.isSubmitSuccessful} />
      <Controller
        control={control}
        name="lpn"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel htmlFor="lpn">{t('trackingObjects.activationForm.lpn')}</FieldLabel>
            <Input id="lpn" {...field} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="label"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel htmlFor="label">{t('trackingObjects.activationForm.label')}</FieldLabel>
            <Input id="label" {...field} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="type"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('trackingObjects.activationForm.type')}</FieldLabel>
            <TrackingObjectTypeSelector value={field.value} onChange={field.onChange} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="msisdn"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel htmlFor="msisdn">{t('trackingObjects.activationForm.msisdn')}</FieldLabel>
            <Input id="msisdn" {...field} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="trackingId"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel htmlFor="trackingId">{t('trackingObjects.activationForm.trackingId')}</FieldLabel>
            {/* Camera barcode scanning is deferred (plan.md §7 #4); manual entry first. */}
            <Input id="trackingId" placeholder={t('trackingObjects.activationForm.trackingIdHelper')} {...field} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="vendorId"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('trackingObjects.activationForm.vendor')}</FieldLabel>
            <VendorSelector value={field.value} onChange={field.onChange} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="deviceModelId"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('trackingObjects.activationForm.deviceModel')}</FieldLabel>
            <VendorModelSelector vendorId={vendorId} value={field.value} onChange={field.onChange} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Button type="submit" disabled={formState.isSubmitting}>
        {formState.isSubmitting
          ? t('trackingObjects.activationForm.submitting')
          : t('trackingObjects.activationForm.submitButton')}
      </Button>
    </form>
  )
}
